import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getBugContext, type ConsoleEntry, type RouteEntry } from "@/lib/bugContext";

export type BugSeverity = "low" | "medium" | "high";
export type BugStatus = "open" | "in_progress" | "resolved" | "wont_fix";
export type BugOcrStatus = "pending" | "completed" | "failed" | null;

export interface BugReport {
  id: string;
  org_id: string;
  reporter_id: string | null;
  title: string;
  description: string | null;
  severity: BugSeverity;
  status: BugStatus;
  page_url: string | null;
  route_history: RouteEntry[];
  viewport_width: number | null;
  viewport_height: number | null;
  user_agent: string | null;
  console_errors: ConsoleEntry[];
  screenshot_path: string | null;
  screenshot_text: string | null;
  screenshot_text_confidence: number | null;
  screenshot_ocr_status: BugOcrStatus;
  screenshot_ocr_error: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BugReportWithReporter extends BugReport {
  reporter: { id: string; display_name: string | null; email: string } | null;
}

interface CreateBugReportInput {
  title: string;
  description?: string;
  severity: BugSeverity;
  screenshot?: File;
}

const BUG_REPORTS_KEY = ["bug-reports"];

async function getCurrentUserAccount() {
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error("Not authenticated");
  const { data: userAccount, error } = await supabase
    .from("user_account")
    .select("id, org_id")
    .eq("external_auth_user", authData.user.id)
    .single();
  if (error || !userAccount) throw new Error("User account not found");
  return userAccount;
}

export function useBugReports() {
  return useQuery({
    queryKey: BUG_REPORTS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bug_report")
        .select("*, reporter:reporter_id(id, display_name, email)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as BugReportWithReporter[];
    },
  });
}

export function useCreateBugReport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateBugReportInput) => {
      const userAccount = await getCurrentUserAccount();
      const context = getBugContext();

      const insertPayload: Record<string, unknown> = {
        org_id: userAccount.org_id,
        reporter_id: userAccount.id,
        title: input.title.trim(),
        description: input.description?.trim() || null,
        severity: input.severity,
        status: "open" as const,
        page_url: context.page_url,
        route_history: context.route_history,
        viewport_width: context.viewport_width,
        viewport_height: context.viewport_height,
        user_agent: context.user_agent,
        console_errors: context.console_errors,
      };

      const { data: created, error: insertError } = await supabase
        .from("bug_report")
        .insert(insertPayload)
        .select()
        .single();

      if (insertError) throw insertError;
      const report = created as unknown as BugReport;

      if (input.screenshot) {
        const ext = input.screenshot.name.split(".").pop()?.toLowerCase() || "png";
        const path = `${userAccount.org_id}/${report.id}/screenshot.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("bug-screenshots")
          .upload(path, input.screenshot, {
            contentType: input.screenshot.type || "image/png",
            upsert: false,
          });

        if (uploadError) {
          // Screenshot upload failed but the report is saved — don't fail the whole flow.
          console.error("Bug screenshot upload failed:", uploadError);
        } else {
          await supabase
            .from("bug_report")
            .update({ screenshot_path: path, screenshot_ocr_status: "pending" })
            .eq("id", report.id);

          // Fire-and-forget OCR. Errors are recorded on the row itself.
          supabase.functions
            .invoke("extract-bug-screenshot", { body: { bug_report_id: report.id } })
            .catch((err) => console.error("OCR invoke failed:", err));
        }
      }

      return report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEY });
      toast({
        title: "Bug report submitted",
        description: "Thanks — we've captured the details.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't submit report",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateBugReportStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BugStatus }) => {
      const userAccount = await getCurrentUserAccount();
      const isResolution = status === "resolved" || status === "wont_fix";

      const updates: Record<string, unknown> = { status };
      if (isResolution) {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by = userAccount.id;
      } else {
        updates.resolved_at = null;
        updates.resolved_by = null;
      }

      const { data, error } = await supabase
        .from("bug_report")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as BugReport;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEY });
    },
    onError: (error: Error) => {
      toast({
        title: "Status update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteBugReport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (report: { id: string; screenshot_path: string | null }) => {
      if (report.screenshot_path) {
        await supabase.storage.from("bug-screenshots").remove([report.screenshot_path]);
      }
      const { error } = await supabase.from("bug_report").delete().eq("id", report.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEY });
      toast({ title: "Bug report deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export async function getBugScreenshotUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("bug-screenshots")
    .createSignedUrl(path, 3600);
  if (error) {
    console.error("Failed to sign bug screenshot URL:", error);
    return null;
  }
  return data.signedUrl;
}
